import React, { useEffect, useRef } from 'react';
import { useTable, usePagination } from "react-table"

import { DEFAULT_PAGE_SIZE } from '../utils/paginationDefaults';

function Table({ columns, data, pageSize: externalPageSize = DEFAULT_PAGE_SIZE, setPageSize: setExternalPageSize }) {
    const props = useTable({
        columns,
        data,
        initialState: { pageSize: externalPageSize }
    }, usePagination);
    const {
        getTableBodyProps,
        rows,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize: internalPageSize }
    } = props;

    const prevExternalPageSize = useRef(externalPageSize);

    // Sincronizar pageSize externo para interno
    useEffect(() => {
        if (prevExternalPageSize.current !== externalPageSize && internalPageSize !== externalPageSize) {
            setPageSize(externalPageSize);
            prevExternalPageSize.current = externalPageSize;
        }
    }, [externalPageSize, internalPageSize, setPageSize]);

    // Determinar nivel baseado nas colunas (verificar se existe coluna de nível)
    const nivel = columns.some(col => col.id === 'nivel') ? 'Administrador' : 'Gestor';

    const paginacao = (
        <div className="table-footer d-flex justify-content-between align-items-center flex-wrap gap-1">
            <span>Exibindo {Math.min(pageIndex * internalPageSize + page.length, rows.length)} de {rows.length} registros</span>
            <div className="text-nowrap">
                <span>Página {pageIndex + 1} de {pageOptions.length}</span>
                <button className="btn btn-primary btn-sm ms-2 text-white" onClick={() => previousPage()} disabled={!canPreviousPage}>
                    <i className="bi bi-caret-left"></i>
                </button>
                <button className="btn btn-primary btn-sm ms-1 text-white" onClick={() => nextPage()} disabled={!canNextPage}>
                    <i className="bi bi-caret-right"></i>
                </button>
            </div>
        </div>
    );

    return (
        <>
            {/* Mobile: lista de cards (tabela não funciona bem em telas estreitas) */}
            <div className="d-md-none mt-3 mb-3">
                <div className="bg-primary text-white rounded-top px-3 py-2 fw-semibold">Lista de Usuários</div>
                <div className="border border-top-0 rounded-bottom p-2 bg-white">
                    {page.length === 0 && (
                        <div className="text-center text-muted py-4">
                            <i className="bi bi-people fs-2 d-block mb-2" />
                            Nenhum usuário encontrado.
                        </div>
                    )}
                    {page.map((row) => {
                        prepareRow(row);
                        const celula = (id) => row.cells.find(c => c.column.id === id);
                        const foto = celula('foto');
                        const nome = celula('nome_completo');
                        const codigo = celula('codigo');
                        const nivelCell = celula('nivel');
                        const email = celula('email');
                        const unidade = celula('unidade');
                        const acoes = celula('actions');
                        return (
                            <div className="card border shadow-sm mb-2" key={row.id}>
                                <div className="card-body p-3">
                                    <div className="d-flex align-items-start gap-2">
                                        {foto && <div className="flex-shrink-0">{foto.render('Cell')}</div>}
                                        <div className="flex-grow-1 min-w-0">{nome && nome.render('Cell')}</div>
                                        {acoes && <div className="flex-shrink-0">{acoes.render('Cell')}</div>}
                                    </div>
                                    <div className="mt-2 pt-2 border-top d-flex flex-column gap-1 fs-7">
                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-1">
                                            <span className="text-muted">Matrícula: <span className="fw-semibold text-primary">{codigo && codigo.render('Cell')}</span></span>
                                            {nivelCell && <span>{nivelCell.render('Cell')}</span>}
                                        </div>
                                        <span className="text-muted text-break">E-mail: <span className="fw-semibold text-primary">{email && email.render('Cell')}</span></span>
                                        <span className="text-muted">Unidade: <span className="fw-semibold text-primary">{unidade && unidade.render('Cell')}</span></span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div className="bg-primary text-white rounded p-2 mt-2">
                        {paginacao}
                    </div>
                </div>
            </div>

            {/* Desktop: tabela */}
            <div className="d-none d-md-block table-full-width mt-3 mb-3">
                <div className="table-responsive">
                    <table className="table align-middle mt-1 table-striped table-hover">
                        <thead>
                            <tr>
                                <th colSpan={nivel === 'Administrador' ? 7 : 6} className='bg-primary text-white rounded-top'>Lista de Usuários</th>
                            </tr>
                        </thead>
                        <thead>
                            <tr className='border border-white text-nowrap'>
                                <th className='text-uppercase text-black-50 fw-medium fs-8 bg-muted'></th>
                                <th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted'>Nome</th>
                                <th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted'>Matrícula</th>
                                {nivel === 'Administrador' &&
                                    <th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted'>Nivel</th>
                                }
                                <th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted'>Email</th>
                                <th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted'>Unidade</th>
                                {nivel === 'Administrador' &&
                                    <th className='text-uppercase text-black-50 fw-bold fs-8 bg-muted'>Ações</th>
                                }
                            </tr>
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {page.map((row, i) => {
                                prepareRow(row);
                                return (
                                    <tr className='text-nowrap' {...row.getRowProps()}>
                                        {row.cells.map(cell => {
                                            return (
                                                <td {...cell.getCellProps()}>
                                                    {cell.render("Cell")}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr>
                                <th colSpan="7" className='bg-primary text-white rounded-bottom'>
                                    {paginacao}
                                </th>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </>
    )

}
export default Table;
